import { SbefElementProperty } from '@shared/templates/categories/common-element-category-properties.model';
import {
  SbefCode,
  SbefEntry,
  SbefGroupCode,
  sbefGroupIds,
} from '@shared/construction/sbef.model';
import {
  ElementPropertyName,
  ElementSelectPropertyCountType,
} from '../models/element-property.model';
import type {
  IElement,
  IProductElement,
  OneOfElements,
  OneOfParentElements,
  OneOfPropertyElements,
  Project,
} from '../models/project.model';
import { isOneOf } from './array.helpers';
import {
  getCount,
  getElementProperty,
  getElementPropertyResolvedCountByNameOrId,
  setElementProperties,
} from './element-property.helpers';
import { isExpressionValue } from './expression-factory.helpers';
import { getId, isPrimitive } from './object.helpers';
import {
  getElementById,
  isElement,
  isOneOfPropertyElements,
  isProductElement,
  type ElementPath,
} from './recursive-element.helpers';
import type { ItemOrItemId, PartialRecord } from '../models/type-helpers.model';
import {
  sbefCodeRecord,
  sbefCodesSet,
} from '@shared/construction/sbef-codes.data';
import { guards } from './typeguards.helpers';
import { isRecipe } from './recipe.helpers';
import { Recipe } from '@shared/models/recipe.model';
import { isElementSbefProperty } from '@shared/templates/categories/common-element-category-properties.helpers';

/**
 * Get a sbef entry object by code
 * @param code
 * @returns
 */
export const getSbefEntryById = (code: SbefCode): SbefEntry | undefined => {
  const sbefCode = getSbefCode(code);
  return sbefCode ? sbefCodeRecord[sbefCode] : undefined;
};

export const getSbefLifetimeById = (id: SbefCode): number | undefined => {
  return getSbefEntryById(id)?.lifetime;
};

/**
 * Get a valid SBEF code from a string (eg "04. Afterlaying" -> "04") or number (eg 101 -> "101")
 * @param count
 * @param fallback
 * @returns
 */
export const getSbefCode = (
  count?: ElementSelectPropertyCountType,
): SbefCode | undefined => {
  const tryCode = guards.is.number(count) ? count.toString() : count;
  return isSbefCode(tryCode) ? tryCode : undefined;
};

/**
 * Get the SBEF group id from an sbef code or label (eg "04. Afterlaying" -> "0")
 * @param value
 * @returns SBEFGroupId if found, otherwise undefined
 */
export const getSbefGroupCode = (
  value?: SbefCode,
): SbefGroupCode | undefined => {
  const code = getSbefCode(value);

  if (guards.is.undefined(code)) return undefined;

  // 100+ is not a valid SBEF group id
  const group = code.length < 3 ? code.charAt(0) : undefined;

  return isSbefGroupCode(group) ? group : undefined;
};

/**
 * Check if a string is an existing SBEF code
 * @param sbefCodeId
 * @returns
 */
export const isSbefCode = (sbefCodeId?: unknown): sbefCodeId is SbefCode =>
  guards.is.string(sbefCodeId) && sbefCodesSet.has(sbefCodeId);

/**
 * Check if a string is a valid SBEF group id
 * @param sbefGroupId
 * @returns
 */
export const isSbefGroupCode = (
  sbefGroupId?: unknown,
): sbefGroupId is SbefGroupCode => isOneOf(sbefGroupIds, sbefGroupId);

/**
 * Get a sbef property from an element
 * @param element
 * @returns
 */
export const getSbefProperty = (
  element: IElement,
): SbefElementProperty | undefined => {
  const property = getElementProperty(element, ElementPropertyName.SbefCode);
  return isElementSbefProperty(property) ? property : undefined;
};

/**
 * Set SBEF code on an element when its category exposes an SBEF property.
 * Returns the root unchanged when the element has no {@link ElementPropertyName.SbefCode}
 * (e.g. Maintenance).
 */
export const setSbefCodeOnElementIfSupported = <
  R extends OneOfParentElements | Project,
>(
  root: R,
  elementOrId: ItemOrItemId<OneOfPropertyElements>,
  sbefCode: SbefCode,
): R => {
  const element = getElementById(root, getId(elementOrId));
  if (!isOneOfPropertyElements(element)) return root;
  if (!getElementProperty(element, ElementPropertyName.SbefCode, false)) {
    return root;
  }
  return setElementProperties(root, element, {
    name: ElementPropertyName.SbefCode,
    count: sbefCode,
  });
};

/**
 * SBEF on the product row, else inherited from the nearest parent {@link IElement}.
 */
export const getSbefCodeForProductElement = (
  productElement: IProductElement,
  path: ElementPath,
): SbefCode | undefined => {
  const onProduct = getSbefCodeFromProductElement(productElement);
  if (onProduct) return onProduct;
  for (let i = path.length - 1; i >= 0; i--) {
    const node = path[i];
    if (isElement(node)) return getSbefCodeFromElementOrRecipe(node);
  }
  return undefined;
};

/**
 * The map's sole SBEF key, or `undefined` when empty or ambiguous (multiple keys).
 * Lets single-SBEF product `characteristics` (e.g. windows keyed `'55'`) resolve even
 * when no SBEF code is set on the element, without guessing between several codes.
 */
export const getSoleSbefKey = (
  characteristicsMap: PartialRecord<SbefCode, unknown> | undefined,
): SbefCode | undefined => {
  if (!characteristicsMap) return undefined;
  const keys = Object.keys(characteristicsMap);
  return keys.length === 1 ? getSbefCode(keys[0]) : undefined;
};

/**
 * SBEF code to key a product's `characteristics` lookup (lifetime/maintenance) by.
 *
 * Prefers the resolved SBEF code from the product row or nearest ancestor element
 * (see {@link getSbefCodeForProductElement}). When none is set — common for
 * Windows & Doors, where users rarely pick an SBEF code — it falls back to the
 * characteristics map's sole key (see {@link getSoleSbefKey}).
 */
export const getCharacteristicsSbefCode = (
  productElement: IProductElement,
  path: ElementPath,
  characteristicsMap: PartialRecord<SbefCode, unknown> | undefined,
): SbefCode | undefined =>
  getSbefCodeForProductElement(productElement, path) ??
  getSoleSbefKey(characteristicsMap);

/**
 * SBEF code from a {@link IProductElement} (not an {@link IElement}).
 */
export const getSbefCodeFromProductElement = (
  element: IProductElement,
): SbefCode | undefined =>
  getSbefCode(
    getElementPropertyResolvedCountByNameOrId(
      element,
      ElementPropertyName.SbefCode,
    ),
  );

/**
 * Read an explicitly set SBEF count from an element property.
 * Does not fall back to the first select option when count is unset — unset SBEF
 * must stay undefined so grouping can fold the element into "Other".
 */
const getExplicitSbefCountFromElement = (
  element: IElement,
): ElementSelectPropertyCountType | undefined => {
  const property = getSbefProperty(element);
  if (!property) return undefined;

  const raw = getCount(property, false);
  if (!guards.is.defined(raw)) return undefined;

  // Pipeline enrichment stores SBEF as expression objects
  if (isExpressionValue(raw)) {
    const resolved = raw.value ?? raw.resolved;
    if (!guards.is.defined(resolved)) return undefined;
    return resolved;
  }

  if (isPrimitive(raw)) return raw;

  return undefined;
};

/**
 * Get the SBEF code (like "04") from an element or recipe.
 * @param element
 * @returns
 */
export const getSbefCodeFromElementOrRecipe = (
  element: OneOfElements | Recipe,
): SbefCode | undefined => {
  if (isRecipe(element)) {
    const maybeCode =
      element.category_property_value_record[ElementPropertyName.SbefCode];

    return guards.is.string(maybeCode) ? getSbefCode(maybeCode) : undefined;
  }
  if (isProductElement(element)) {
    return getSbefCodeFromProductElement(element);
  }
  if (isElement(element)) {
    return getSbefCode(getExplicitSbefCountFromElement(element));
  }

  return undefined;
};

/**
 * Get the sbef group code from an element
 * @param element
 * @returns
 */
export const getSbefGroupCodeFromElementOrRecipe = (
  element: OneOfElements | Recipe,
): SbefGroupCode | undefined =>
  getSbefGroupCode(getSbefCodeFromElementOrRecipe(element));

/**
 * Get the label from a sbef code
 * @param option
 * @returns
 */
export function getSbefLabel(
  code: SbefCode,
  options?: { includeDescription?: boolean },
): string;
export function getSbefLabel(
  maybeCode?: string,
  options?: {
    includeDescription?: boolean;
    fallback?: string;
  },
): string;
export function getSbefLabel(
  maybeCode?: SbefCode | string,
  options?: {
    includeDescription?: boolean;
    fallback?: string;
  },
): string {
  if (!isSbefCode(maybeCode)) return options?.fallback ?? '-';
  const data = getSbefDataByCode(maybeCode);
  return [
    data.code,
    data.name,
    options?.includeDescription === true ? data.description : undefined,
  ]
    .filter(guards.is.defined)
    .join('. ');
}

export const getSbefDataByCode = (code: SbefCode): SbefEntry => {
  return sbefCodeRecord[code];
};

/** True when an SBEF code has no display name (e.g. placeholder `14.`). */
export const isUnlabeledSbefCode = (code: SbefCode): boolean =>
  getSbefDataByCode(code).name.trim() === '';

/**
 * Map unlabeled child codes to their parent group digit (e.g. `14` → `1`).
 * Labeled codes are returned unchanged.
 */
export const normalizeUnlabeledSbefCode = (code: SbefCode): SbefCode => {
  if (!isUnlabeledSbefCode(code)) return code;
  const group = getSbefGroupCode(code);
  return group ?? code;
};

/** Parse a stored SBEF property count (code id or legacy `NN. label` string). */
export const parseSbefCodeFromPropertyCount = (
  count?: ElementSelectPropertyCountType,
): SbefCode | undefined => {
  const direct = getSbefCode(count);
  if (direct) return direct;
  const parsed = count?.toString().split('.')[0];
  return isSbefCode(parsed) ? parsed : undefined;
};

/**
 * Normalize a stored SBEF property count, folding unlabeled codes to the parent group.
 */
export const normalizeSbefPropertyCount = (
  count?: ElementSelectPropertyCountType,
): SbefCode | undefined => {
  const code = parseSbefCodeFromPropertyCount(count);
  if (!code) return undefined;
  return normalizeUnlabeledSbefCode(code);
};

/** True when a code should appear in the SBEF property selector menu. */
export const isSbefPropertySelectorCode = (code: SbefCode): boolean =>
  !SBEF_SELECTOR_HIDDEN_CODES.has(code) && !isUnlabeledSbefCode(code);

/** Legacy empty common-works codes hidden from the property selector (replaced by 101–105). */
export const SBEF_SELECTOR_HIDDEN_CODES: ReadonlySet<SbefCode> = new Set([
  '91',
  '92',
  '93',
  '94',
  '95',
  '96',
  '97',
  '98',
  '99',
]);

/** Prefix shown in the SBEF property selector (stored code is unchanged). */
export const getSbefSelectorDisplayCode = (code: SbefCode): string => code;

/**
 * Sort key for the SBEF property menu: group header (e.g. `2.`) immediately before its
 * children (`20.`–`29.`), with 101–105 slotted after `90.` under group 9.
 */
export const getSbefPropertySortKey = (code: SbefCode): number => {
  if (code.length === 1) return Number(code) * 1000;

  const num = Number(code);
  if (num >= 100) return 9000 + (num - 99);

  const group = Math.floor(num / 10);
  return group * 1000 + (num % 10) + 1;
};

const COMBINED_STORED_NAME = 'Combined';

/**
 * Selector name for a stored `Combined` *0 code: `{parent} Combined`.
 * Uses the first comma-separated part of the group name so
 * "Demolition, remediation" becomes "Demolition Combined".
 * Codes that already have a specific name (70, 90) are left unchanged.
 */
const getSbefSelectorItemName = (
  code: SbefCode,
  storedName: string,
): string => {
  if (storedName !== COMBINED_STORED_NAME) return storedName;
  const group = getSbefGroupCode(code);
  if (!group || group === code) return storedName;
  const parentLead = getSbefDataByCode(group).name.split(',')[0]?.trim();
  if (!parentLead) return storedName;
  return `${parentLead} Combined`;
};

/**
 * Label for the SBEF code property menu. Combined *0 codes include the parent
 * group name (e.g. `30. Frame Combined`). 70 and 90 keep their stored names.
 */
export const getSbefSelectorLabel = (
  code: SbefCode,
  options?: { includeDescription?: boolean },
): string => {
  const data = getSbefDataByCode(code);
  return [
    getSbefSelectorDisplayCode(code),
    getSbefSelectorItemName(code, data.name),
    options?.includeDescription === true ? data.description : undefined,
  ]
    .filter(guards.is.defined)
    .join('. ');
};
